import { useCallback, useEffect, useRef, useState } from 'react';
import { applePlatform, isAndroid } from './platform.js';

/*
 * The Presentation API is how a page offers its own cast button: Chrome and
 * Edge surface Chromecast (and other cast receivers) through it, and the
 * receiver loads the URL we hand it. Only the slice we use is declared here —
 * it is not in lib.dom.
 */
type PresentationConnectionState = 'connecting' | 'connected' | 'closed' | 'terminated';

interface PresentationConnection extends EventTarget {
  readonly id: string;
  readonly state: PresentationConnectionState;
  close(): void;
  terminate(): void;
}

interface PresentationAvailability extends EventTarget {
  readonly value: boolean;
}

interface PresentationRequest extends EventTarget {
  start(): Promise<PresentationConnection>;
  reconnect(id: string): Promise<PresentationConnection>;
  getAvailability(): Promise<PresentationAvailability>;
}

type PresentationRequestCtor = new (urls: string | string[]) => PresentationRequest;

const CAST_CONNECTION = 'tvcasino.castConnectionId';

export type CastStatus = 'unsupported' | 'idle' | 'connecting' | 'casting';

/**
 * Why the API is missing. `insecure` is the common one: PresentationRequest is
 * secure-context only, so a LAN page served over plain http never gets it.
 */
export type CastBlock = 'insecure' | 'browser' | 'mobile' | 'ios' | 'mac' | null;

export interface CastControls {
  status: CastStatus;
  /** Chrome has seen at least one cast receiver on the network. */
  available: boolean;
  /**
   * True once the first availability check has settled. Until then `available`
   * is only the initial guess, so "no devices" messaging must wait on this.
   */
  checked: boolean;
  block: CastBlock;
  error: string | null;
  start: () => void;
  stop: () => void;
}

const ctor = (): PresentationRequestCtor | null =>
  typeof window !== 'undefined' && 'PresentationRequest' in window
    ? (window as unknown as { PresentationRequest: PresentationRequestCtor }).PresentationRequest
    : null;

const blockedBy = (): CastBlock => {
  if (typeof window === 'undefined') return 'browser';
  // The platforms get named before the HTTPS and API checks: an iPhone or
  // Safari on a Mac has no PresentationRequest at all, and Chrome on Android
  // implements the API but will only present registered `cast:` receiver
  // apps, never a web page. None of their ways out need HTTPS, so a plain-http
  // LAN page must not send them to Chrome's tab cast instead.
  const apple = applePlatform();
  if (apple) return apple;
  if (isAndroid()) return 'mobile';
  if (!window.isSecureContext) return 'insecure';
  if (!ctor()) return 'browser';
  return null;
};

const session = {
  get: () => {
    try {
      return sessionStorage.getItem(CAST_CONNECTION);
    } catch {
      return null;
    }
  },
  set: (id: string | null) => {
    try {
      if (id === null) sessionStorage.removeItem(CAST_CONNECTION);
      else sessionStorage.setItem(CAST_CONNECTION, id);
    } catch {
      /* ignore */
    }
  },
};

/**
 * Casts `url` to a TV. The receiver loads that URL itself, so it must be
 * reachable from the cast device — a LAN IP, never localhost.
 */
export function useCast(url: string | null): CastControls {
  const [block] = useState<CastBlock>(blockedBy);
  const [status, setStatus] = useState<CastStatus>(() => (blockedBy() === null ? 'idle' : 'unsupported'));
  const [available, setAvailable] = useState(false);
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef<PresentationRequest | null>(null);
  const connectionRef = useRef<PresentationConnection | null>(null);

  const bind = useCallback((connection: PresentationConnection) => {
    connectionRef.current = connection;
    session.set(connection.id);
    setStatus(connection.state === 'connected' ? 'casting' : 'connecting');
    setError(null);

    const onOpen = () => setStatus('casting');
    const onEnd = () => {
      connectionRef.current = null;
      session.set(null);
      setStatus('idle');
    };
    connection.addEventListener('connect', onOpen);
    connection.addEventListener('close', onEnd);
    connection.addEventListener('terminate', onEnd);
  }, []);

  useEffect(() => {
    const Ctor = ctor();
    if (!Ctor || !url || block !== null) return;

    // A live cast keeps its own request object: rebuilding it would drop the TV.
    if (connectionRef.current) return;

    let request: PresentationRequest;
    try {
      request = new Ctor(url);
    } catch {
      setStatus('unsupported');
      return;
    }
    requestRef.current = request;

    let cancelled = false;
    let availability: PresentationAvailability | null = null;
    const onChange = () => setAvailable(availability?.value ?? false);

    request
      .getAvailability()
      .then((a) => {
        if (cancelled) return;
        availability = a;
        setAvailable(a.value);
        setChecked(true);
        a.addEventListener('change', onChange);
      })
      .catch(() => {
        // Some platforms refuse monitoring but can still cast — offer the button.
        if (!cancelled) {
          setAvailable(true);
          setChecked(true);
        }
      });

    // Survive a refresh of this tab without dropping the TV.
    const savedId = session.get();
    if (savedId) {
      request
        .reconnect(savedId)
        .then((c) => {
          if (!cancelled) bind(c);
        })
        .catch(() => session.set(null));
    }

    return () => {
      cancelled = true;
      availability?.removeEventListener('change', onChange);
    };
  }, [url, bind, block]);

  const start = useCallback(() => {
    const request = requestRef.current;
    if (!request) return;
    setError(null);
    setStatus('connecting');
    request
      .start()
      .then(bind)
      .catch((err: unknown) => {
        const name = err instanceof Error ? err.name : '';
        setStatus('idle');
        // Dismissing the device picker is not an error worth showing.
        if (name === 'AbortError' || name === 'NotAllowedError') return;
        setError(
          name === 'NotFoundError'
            ? 'No cast devices found on this network.'
            : 'Could not start casting.',
        );
      });
  }, [bind]);

  const stop = useCallback(() => {
    const connection = connectionRef.current;
    connectionRef.current = null;
    session.set(null);
    setStatus('idle');
    connection?.terminate();
  }, []);

  return { status, available, checked, block, error, start, stop };
}
